/* eslint-disable import/no-cycle */

import React from 'react';
import { FunctionField, ReferenceField, TextField } from 'react-admin';
import { Box, IconButton, useTheme } from '@material-ui/core';
import moment from 'moment';
import { Visibility } from '@material-ui/icons';
import resourceSlug from '../../constant/resource-slug';
import InvoiceListActions from './components/InvoiceList/InvoiceListActions';
import { CustomizedNoPageList } from '../../base/components/customized/CustomizedNoPageList';
import InvoiceFilters from './components/InvoiceList/InvoiceFilters';
import InvoiceStatus from './components/InvoiceStatus';
import { DateField } from '../../base/components/ra/fields';
import { DATE_DISPLAY_FORMAT } from '../../constant';
import AmountField from '../../base/components/ra/fields/amount.field';
import PaymentStatus from '../payment/components/PaymentStatus';
import { hasPermission } from '../../services/util/auth';
import ExportButton from './components/ExportButton';
import ValidateButton from './components/InvoiceList/ValidateButton';
import RegenerateButton from './components/InvoiceList/RegenerateButton';
import InvoiceBulkActionButtons from './components/InvoiceList/InvoiceBulkActionButtons';
import { useInvoiceIdsSync, useInvoiceStatus } from './hooks';
import { PAYMENT_STATUS } from '../../constant/invoice';
import { formatDate } from '../../services/util/date';
import PaymentButton from './components/InvoiceList/PaymenButton';
import AdjustInvoiceButton from './components/InvoiceList/AdjustInvoiceButton';

const InvoiceList = (props) => {
  const theme = useTheme();
  const hasReadPermission = hasPermission(resourceSlug.INVOICE, 'read');

  const {
    isExportableInvoice,
    isPayableInvoice,
    isRegeneratableInvoice,
    isValidatableInvoice,
    isAdjusmentEditableInvoice,
  } = useInvoiceStatus();

  const { setInvoiceIds } = useInvoiceIdsSync();

  return (
    <>
      <CustomizedNoPageList
        {...props}
        filters={<InvoiceFilters />}
        actions={<InvoiceListActions />}
        disabledAutoRefetch
        syncWithLocation
        rowClick={false}
        bulkActionButtons={<InvoiceBulkActionButtons setInvoiceIds={setInvoiceIds} />}
        sort={{
          field: 'invoiceDate',
          order: 'DESC',
        }}
        isRowDisabled={(record) => !isPayableInvoice(record)}
      >
        <TextField label="resources.invoice.fields.invoiceNo" sortable={false} source="invoiceNo" />
        <ReferenceField
          label="resources.group.name"
          sortable={false}
          reference={resourceSlug.GROUP}
          source="groupId"
          link="show"
        >
          <TextField source="name" />
        </ReferenceField>
        <DateField
          source="invoiceDate"
          sortable={false}
          label="resources.invoice.fields.invoiceDate"
          dateFormat={DATE_DISPLAY_FORMAT}
        />
        <FunctionField
          label="resources.invoice.fields.dueDate"
          sortable={false}
          render={(record) =>
            record.dueDate ? (
              <Box
                color={
                  moment.unix(record.dueDate).isSameOrBefore(moment()) && record.paymentStatus !== PAYMENT_STATUS.PAID
                    ? theme.palette.error.main
                    : undefined
                }
              >
                {formatDate(record.dueDate, DATE_DISPLAY_FORMAT)}
              </Box>
            ) : (
              '-'
            )
          }
        />
        <FunctionField
          sortable={false}
          label="resources.invoice.fields.invoicePeriod"
          render={(record) =>
            `${formatDate(record.invoiceFrom, DATE_DISPLAY_FORMAT)} - ${formatDate(record.invoiceTo, DATE_DISPLAY_FORMAT)}`
          }
        />
        <AmountField
          source="invoiceAmount"
          sortable={false}
          label="resources.invoice.fields.amount"
          symbol={(record) => record.currencyCode}
        />
        <FunctionField
          label="resources.payment.fields.status"
          sortable={false}
          render={(record) => <PaymentStatus status={record.paymentStatus} />}
        />
        <FunctionField
          sortable={false}
          label="resources.invoice.fields.status"
          render={(record) => <InvoiceStatus status={record.invoiceStatus} />}
        />
        <FunctionField
          label="ra.field.action"
          sortable={false}
          render={(record) => (
            <Box display="flex" gap={4} justifyContent="center">
              {hasReadPermission && (
                <Box
                  component={IconButton}
                  size="small"
                  minWidth={30}
                  href={`/#/${resourceSlug.INVOICE}/${record.id}/show`}
                >
                  <Visibility fontSize="small" />
                </Box>
              )}
              {isValidatableInvoice(record) && <ValidateButton invoiceId={record.id} />}
              {isAdjusmentEditableInvoice(record) && <AdjustInvoiceButton record={record} />}
              {isRegeneratableInvoice(record) && <RegenerateButton invoiceId={record.id} />}
              {isPayableInvoice(record) && <PaymentButton invoiceId={record.id} />}
              {isExportableInvoice() && <ExportButton invoiceId={record.id} />}
            </Box>
          )}
        />
      </CustomizedNoPageList>
    </>
  );
};

export default InvoiceList;
